import { useState } from 'react'
import { useT, useI18n } from '../../i18n/useT'
import { Logo } from './Logo'
import { ThemePicker } from './ThemePicker'
import { openConsentSettings } from '../../lib/consent'
import { lingvanexUrl } from '../../lib/lingvanex'

/** Internal links to the static SEO landing pages (generated at build time) —
 * they keep those pages crawlable from the app itself.
 * All 8 format landings on purpose: the niche ones (ASS/SSA/MicroDVD/MPL2/
 * TMP) have no external links anywhere, so internal ones are the only crawl
 * path besides the sitemap. The column collapses visually to match the other
 * columns' height — hidden links stay in the DOM, so crawlers see them all.
 * The first TOOLS_VISIBLE entries are the ones shown collapsed. */
const TOOLS_VISIBLE = 6

function FooterColumn({
  title,
  links,
  collapsedCount,
  moreLabel,
  lessLabel,
}: {
  title: string
  /** A link, or an in-page action when there is nowhere to navigate
   * (cookie settings open a dialog) */
  links: { label: string; href?: string; onClick?: () => void }[]
  /** Show only this many links until expanded; the rest stay in the DOM
   * (just hidden) so crawlers follow them either way */
  collapsedCount?: number
  moreLabel?: string
  lessLabel?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const collapsible = collapsedCount !== undefined && links.length > collapsedCount
  return (
    <div>
      <h3 className="text-sm font-bold text-text uppercase tracking-wide mb-4">{title}</h3>
      <ul className="space-y-3">
        {links.map((link, i) => {
          const external = !!link.href && link.href.startsWith('http')
          const hidden = collapsible && !expanded && i >= collapsedCount
          const style = 'text-sm text-text-secondary hover:text-text transition-colors'
          return (
            <li key={link.href ?? link.label} className={hidden ? 'hidden' : undefined}>
              {link.href ? (
                <a
                  href={link.href}
                  className={style}
                  target={external ? '_blank' : undefined}
                  rel={external ? 'noopener noreferrer' : undefined}
                >
                  {link.label}
                </a>
              ) : (
                <button type="button" onClick={link.onClick} className={`${style} cursor-pointer`}>
                  {link.label}
                </button>
              )}
            </li>
          )
        })}
      </ul>
      {collapsible && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 inline-flex items-center gap-1 text-sm text-text-muted hover:text-text transition-colors cursor-pointer"
          aria-expanded={expanded}
        >
          {expanded ? lessLabel : moreLabel}
          <svg
            className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
    </div>
  )
}

export function Footer() {
  const t = useT()
  const { locale } = useI18n()

  // Localized static sets exist for every UI language (the SEO generator
  // emits /{code}/… for each locale catalog, and all 19 non-EN UI codes are
  // among them) — point the reader at the pages in their own language
  const seo = locale === 'en' ? '' : `/${locale}`

  // Order matters twice over: the first TOOLS_VISIBLE entries are what the
  // column shows before "Show more", and the whole list is the crawl path to
  // pages that have no external links anywhere. Video leads, because that is
  // what the product leads with now; the niche formats trail. Every entry
  // exists in every interface language — the video set is generated for
  // exactly these 20 (`uiOnly` in the generator), which is why nothing here
  // has to be hidden per locale any more.
  const toolLinks = [
    // Straight to the tool, in every language. This column is a crawl path,
    // but a person clicking a label expects the thing the label names, and
    // "Subtitle Converter" names a converter — not a page about one. English
    // readers used to land on `/subtitle-converter/` while everyone else got
    // the tool: one label, two destinations, and the English half was the
    // marketing page. The landing keeps its crawl links regardless — the 8
    // English audio-to-* pages and the 7 direction pages all point at it.
    { label: t('conv.title'), href: '/convert' },
    { label: t('pat.xToText', { name: t('pat.video') }), href: `${seo}/video-to-text/` },
    { label: t('pat.xToText', { name: t('pat.audio') }), href: `${seo}/audio-to-text/` },
    { label: t('pat.videoToFmt', { fmt: 'SRT' }), href: `${seo}/video-to-srt/` },
    { label: t('pat.videoToFmt', { fmt: 'VTT' }), href: `${seo}/video-to-vtt/` },
    { label: t('pat.videoToFmt', { fmt: 'ASS' }), href: `${seo}/video-to-ass/` },
    { label: t('pat.audioToFmt', { fmt: 'SRT' }), href: `${seo}/audio-to-srt/` },
    { label: t('pat.audioToFmt', { fmt: 'VTT' }), href: `${seo}/audio-to-vtt/` },
    { label: t('pat.audioToFmt', { fmt: 'ASS' }), href: `${seo}/audio-to-ass/` },
    { label: t('pat.xToText', { name: 'MP3' }), href: `${seo}/mp3-to-text/` },
    { label: t('pat.xToText', { name: 'MP4' }), href: `${seo}/mp4-to-text/` },
    { label: t('pat.videoToFmt', { fmt: 'SSA' }), href: `${seo}/video-to-ssa/` },
    { label: t('pat.videoToFmt', { fmt: 'MicroDVD' }), href: `${seo}/video-to-microdvd/` },
    { label: t('pat.videoToFmt', { fmt: 'MPL2' }), href: `${seo}/video-to-mpl2/` },
    { label: t('pat.videoToFmt', { fmt: 'TMP' }), href: `${seo}/video-to-tmp/` },
    { label: t('pat.audioToFmt', { fmt: 'SSA' }), href: `${seo}/audio-to-ssa/` },
    { label: t('pat.audioToFmt', { fmt: 'MicroDVD' }), href: `${seo}/audio-to-microdvd/` },
    { label: t('pat.audioToFmt', { fmt: 'MPL2' }), href: `${seo}/audio-to-mpl2/` },
    { label: t('pat.audioToFmt', { fmt: 'TMP' }), href: `${seo}/audio-to-tmp/` },
    { label: t('pat.allLanguages'), href: `${seo}/transcribe/` },
  ]

  // Outbound links land on the main site's page in the reader's language
  const lv = (path: string) => lingvanexUrl(path, locale)

  const productLinks = [
    { label: t('pat.translatorFor', { platform: 'MacOS' }), href: lv('/products/macos-translator/') },
    { label: t('pat.translatorFor', { platform: 'Windows' }), href: lv('/products/windows-translator/') },
    { label: t('pat.translatorFor', { platform: 'iOS' }), href: lv('/products/iphone-translator/') },
    { label: t('pat.translatorFor', { platform: 'Android' }), href: lv('/products/android-translator/') },
    { label: t('footer.translatorBrowser'), href: lv('/products/all-extensions/') },
    { label: t('footer.phoneCall'), href: lv('/products/phone-call-translator/') },
  ]

  const legalLinks = [
    { label: t('footer.tos'), href: lv('/terms-of-service/') },
    // privacy-policy has no locale versions on the main site — link it bare
    { label: t('footer.privacyPolicy'), href: 'https://lingvanex.com/privacy-policy/' },
    { label: t('footer.cookies'), href: lv('/cookies-policy/') },
    { label: t('footer.imprint'), href: lv('/imprint/') },
    // The only way to withdraw consent once it is given. On phones the
    // floating disc is hidden (it lands on the pricing cards), so this is the
    // way in there — which is why it lives in the footer and not behind a
    // breakpoint of its own.
    { label: t('cookies.settings'), onClick: openConsentSettings },
  ]

  const companyLinks = [
    { label: t('footer.about'), href: lv('/about-us/') },
    { label: t('footer.partners'), href: lv('/all-partners/') },
    { label: t('footer.supportedLangs'), href: lv('/language-features/') },
    { label: t('footer.translate'), href: lv('/translate/') },
    { label: t('footer.blog'), href: lv('/blog/') },
    { label: t('footer.support'), href: lv('/support/') },
  ]

  return (
    <footer className="border-t border-border bg-surface">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {/* The theme sits UNDER the wordmark rather than across from it: the
            two have nothing to do with each other, and opposite ends of one
            line reads as a pair. Stacked, the logo stays the footer's
            opening and the control is just the first small thing in it. */}
        <div className="flex flex-col items-start gap-4 mb-10">
          <a
            href={lv('/')}
            target="_blank"
            rel="noopener noreferrer"
            title={t('footer.logoTitle')}
            className="inline-flex items-center"
          >
            <Logo className="h-6 w-auto text-logo" />
          </a>
          <ThemePicker />
        </div>

        {/* justify-between so the last column ends at the container's right
            edge — an equal-width grid leaves a lopsided right margin */}
        <div className="flex flex-col gap-10 sm:flex-row sm:flex-wrap sm:justify-between">
          <FooterColumn
            title={t('footer.tools')}
            links={toolLinks}
            collapsedCount={TOOLS_VISIBLE}
            moreLabel={t('footer.showMore')}
            lessLabel={t('footer.showLess')}
          />
          <FooterColumn title={t('footer.products')} links={productLinks} />
          <FooterColumn title={t('footer.legal')} links={legalLinks} />
          <FooterColumn title={t('footer.company')} links={companyLinks} />
        </div>

        <div className="border-t border-border mt-10 pt-6 text-center">
          <p className="text-xs text-text-muted">
            &copy; {new Date().getFullYear()} Lingvanex. {t('footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  )
}
